'use strict';

/**
 * MenubarNavigation - Gère la navigation avec support tactile/desktop et mégamenus
 *
 * Fonctionnalités :
 * - Navigation au clavier (flèches, Escape)
 * - Hover desktop & click mobile
 * - Inert automatique pour l'accessibilité
 * - Mégamenus classiques : tous les enfants s'ouvrent/ferment ensemble
 * - Mégamenus tabs : premier enfant ouvert par défaut, toggle exclusif au clic
 */
class MenubarNavigation {
    /**
     * @param {HTMLElement} navNode - L'élément <nav> contenant le menu
     */
    constructor(navNode) {
        this.nav = navNode;
        this.isMobile = false;
        this._bound = new Set();
        this._openSubmenus = [];

        this.nav.classList.remove('js-not-loaded');

        this._bindOutsideClick();
        this._bindResize();
        this._checkMobile();
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    /**
     * Récupère le submenu associé à un toggle via aria-controls
     * @param {HTMLElement} toggle
     * @returns {HTMLElement|null}
     */
    _getSubmenu(toggle) {
        if (!toggle) return null;
        const id = toggle.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;
    }

    /**
     * Récupère le toggle parent d'un MegaMenuTabs
     *
     * @param {HTMLElement} toggle
     * @returns {HTMLElement|null}
     */
    _getMegaTabsToggle(toggle) {
        const megaTabs = toggle.closest(MenubarNavigation.SEL.megaTabs);

        return megaTabs
            ? megaTabs.querySelector(MenubarNavigation.SEL.scopeToggle)
            : null;
    }

    /**
     * Vérifie si un toggle est un MÉGAMENU CLASSIQUE (is-mega-menu)
     * @param {HTMLElement} toggle
     * @returns {boolean}
     */
    _isMegaMenu(toggle) {
        return toggle.classList.contains(MenubarNavigation.SEL.megaToggle);
    }

    /**
     * Vérifie si un toggle est un MÉGAMENU TABS (is-mega-menu-tabs)
     * @param {HTMLElement} toggle
     * @returns {boolean}
     */
    _isMegaMenuTabs(toggle) {
        return toggle.classList.contains(MenubarNavigation.SEL.megaTabToggle);
    }

    /**
     * Vérifie si un toggle est un enfant d'un MÉGAMENU CLASSIQUE
     * (non cliquable en desktop, sauf mode tabs)
     * @param {HTMLElement} toggle
     * @returns {boolean}
     */
    _isMegaSubmenuChild(toggle) {
        return (
            !this.isMobile &&
            !this._isMegaMenu(toggle) &&
            !!toggle.closest(MenubarNavigation.SEL.megaMenu)
        );
    }

    /**
     * Vérifie si un toggle est un enfant d'un MÉGAMENU TABS
     * @param {HTMLElement} toggle
     * @returns {boolean}
     */
    _isMegaTabsChild(toggle) {
        // Exclut le parent lui-même
        if (this._isMegaMenuTabs(toggle)) return false;

        // En mobile, pas d'enfants "tabs"
        if (this.isMobile) return false;

        // C'est un enfant si inside .mega-menu-tabs
        return !!toggle.closest(MenubarNavigation.SEL.megaTabs);
    }

    /**
     * Trouve le <li> parent au niveau du menubar
     * @param {HTMLElement} target
     * @returns {HTMLElement|null}
     */
    _closestTopItem(target) {
        if (!this._menubar) return null;
        let current = target.closest(MenubarNavigation.SEL.li);
        while (current) {
            if (current.parentElement === this._menubar) return current;
            current = current.parentElement.closest(MenubarNavigation.SEL.li);
        }
        return null;
    }

    /**
     * Met à jour les attributs inert sur nav/main/footer
     */
    _updateInert() {
        const shouldInert = this.isMobile && !this._isNavOpen();
        this.nav.inert = shouldInert;
        if (this._main) this._main.inert = this._isNavOpen();
        if (this._footer) this._footer.inert = this._isNavOpen();
    }

    /**
     * Vérifie si la nav mobile est ouverte
     * @returns {boolean}
     */
    _isNavOpen() {
        return this._burger && this._burger.getAttribute('aria-expanded') === 'true';
    }

    // ─── Lazy Getters ─────────────────────────────────────────────────────────

    get _burger() {
        if (!this.__burger) {
            this.__burger = document.querySelector(MenubarNavigation.SEL.burger);
        }
        return this.__burger;
    }

    get _menubar() {
        if (!this.__menubar) {
            this.__menubar = this.nav.querySelector(MenubarNavigation.SEL.menubar);
        }
        return this.__menubar;
    }

    get _main() {
        if (!this.__main) {
            this.__main = document.querySelector(MenubarNavigation.SEL.main);
        }
        return this.__main;
    }

    get _footer() {
        if (!this.__footer) {
            this.__footer = document.querySelector(MenubarNavigation.SEL.footer);
        }
        return this.__footer;
    }

    // ─── Binding ──────────────────────────────────────────────────────────────

    /**
     * Bind les événements click et keydown sur la nav
     */
    _bindNavDelegation() {
        if (this._bound.has('nav')) return;
        this._bound.add('nav');

        this.nav.addEventListener('click', (e) => {
            const toggle = e.target.closest(MenubarNavigation.SEL.toggle);
            if (!toggle) return;

            // Enfants de mégamenu classique : bloqués en desktop
            if (this._isMegaSubmenuChild(toggle)) {
                e.preventDefault();
                return;
            }

            this._onToggleClick(toggle);
        });

        this.nav.addEventListener('keydown', (e) => this._onKeydown(e));

        this.nav.addEventListener('focusout', (e) => {
            if (!this.nav.contains(e.relatedTarget)) {
                this._closeAllSubmenus();
            }
        });
    }

    /**
     * Bind le bouton burger
     */
    _bindBurger() {
        if (this._bound.has('burger') || !this._burger) return;
        this._bound.add('burger');
        this._burger.addEventListener('click', () => this._setNavOpen(!this._isNavOpen()));
    }

    /**
     * Bind les événements hover (desktop uniquement)
     * Les enfants de mégamenu TABS sont exclus du hover
     */
    _bindHover() {
        if (this._bound.has('hover')) return;
        this._bound.add('hover');

        this.nav.addEventListener('mouseenter', (e) => {
            if (this.isMobile) return;

            const li = e.target.closest(MenubarNavigation.SEL.li);
            if (!li) return;

            const toggle = li.querySelector(MenubarNavigation.SEL.scopeToggle);
            if (!toggle) return;

            // Exclure les enfants de mega-menu & mega-menu-tabs du hover
            if (this._isMegaSubmenuChild(toggle) || this._isMegaTabsChild(toggle)) return;

            const submenu = this._getSubmenu(toggle);
            if (submenu && submenu.hidden === true) {
                this._setSubmenuOpen(toggle, submenu, true);
            }
        }, true);

        this.nav.addEventListener('mouseleave', (e) => {
            if (this.isMobile) return;

            const li = e.target.closest(MenubarNavigation.SEL.li);
            if (!li) return;

            const toggle = li.querySelector(MenubarNavigation.SEL.scopeToggle);
            if (!toggle) return;

            // Exclure les enfants de mega-menu & mega-menu-tabs du hover
            if (this._isMegaSubmenuChild(toggle) || this._isMegaTabsChild(toggle)) return;

            const submenu = this._getSubmenu(toggle);
            if (submenu && submenu.hidden === false && !li.contains(e.relatedTarget)) {
                this._setSubmenuOpen(toggle, submenu, false);
            }
        }, true);
    }

    /**
     * Ferme les menus en cliquant en dehors
     */
    _bindOutsideClick() {
        document.addEventListener('pointerdown', (e) => {
            const insideBurger = this._burger && this._burger.contains(e.target);

            if (!this.nav.contains(e.target) && !insideBurger) {
                this._closeAllSubmenus();
                if (this.isMobile && this._isNavOpen()) {
                    this._setNavOpen(false);
                }
            }
        });
    }

    /**
     * Observe les changements de taille pour déterminer mobile/desktop
     */
    _bindResize() {
        const ro = new ResizeObserver(() => this._checkMobile());
        ro.observe(document.body);
    }

    /**
     * Détermine si on est en mode mobile et bind les listeners appropriés
     */
    _checkMobile() {
        const wasMobile = this.isMobile;
        this.isMobile = this._isBurgerVisible();

        this._bindNavDelegation();

        if (this.isMobile) {
            this._bindBurger();
            this._closeAllSubmenus();
        } else {
            this._bindHover();
            this.nav.inert = false;
            if (this._main) this._main.inert = false;
            if (this._footer) this._footer.inert = false;
        }

        if (wasMobile && !this.isMobile) {
            this._setNavOpen(false);
        }

        if (this.isMobile) {
            this._updateInert();
        }
    }

    /**
     * Vérifie si le burger est visible (CSS-aware)
     * @returns {boolean}
     */
    _isBurgerVisible() {
        if (!this._burger) return false;

        if (typeof this._burger.checkVisibility === 'function') {
            return this._burger.checkVisibility();
        }

        const s = window.getComputedStyle(this._burger);
        return s.display !== 'none' && s.visibility !== 'hidden' && parseFloat(s.opacity) > 0;
    }

    // ─── State Management ──────────────────────────────────────────────────────

    /**
     * Ouvre/ferme la navigation mobile
     * @param {boolean} open
     */
    _setNavOpen(open) {
        if (this._burger) {
            this._burger.setAttribute('aria-expanded', String(open));
        }

        if (open) {
            if (this._main) this._main.setAttribute('aria-hidden', 'true');
            if (this._footer) this._footer.setAttribute('aria-hidden', 'true');

            const firstFocusable = this._menubar && this._menubar.querySelector(MenubarNavigation.SEL.focusable);
            if (firstFocusable) firstFocusable.focus();
        } else {
            if (this._main) this._main.removeAttribute('aria-hidden');
            if (this._footer) this._footer.removeAttribute('aria-hidden');
            this._closeAllSubmenus();
            if (this._burger) this._burger.focus();
        }

        this._updateInert();
    }

    /**
     * Ouvre/ferme un submenu
     * @param {HTMLElement} toggle
     * @param {HTMLElement} submenu
     * @param {boolean} open
     */
    _setSubmenuOpen(toggle, submenu, open) {
        if (open) {
            this._closeSiblingSubmenus(toggle);
            this._openSubmenus.push({ toggle, submenu });
        } else {
            this._openSubmenus = this._openSubmenus.filter((item) => item.toggle !== toggle);
        }

        toggle.setAttribute('aria-expanded', String(open));
        submenu.hidden = !open;

        // Mégamenu CLASSIQUE : ouvre/ferme tous les enfants
        if (this._isMegaMenu(toggle) && !this.isMobile) {
            this._setMegaSubmenuOpen(submenu, open);
        }
        // Mégamenu TABS : ouvre uniquement le premier enfant
        else if (this._isMegaMenuTabs(toggle) && !this.isMobile) {
            this._setMegaTabsSubmenuOpen(submenu, open);
        }
    }

    /**
     * Ouvre/ferme tous les enfants d'un MÉGAMENU CLASSIQUE
     * @param {HTMLElement} submenu
     * @param {boolean} open
     */
    _setMegaSubmenuOpen(submenu, open) {
        const toggles = submenu.querySelectorAll(MenubarNavigation.SEL.toggle);
        for (const toggle of toggles) {
            const childSubmenu = this._getSubmenu(toggle);
            if (childSubmenu) {
                toggle.setAttribute('aria-expanded', String(open));
                childSubmenu.hidden = !open;
            }
        }
    }

    /**
     * Ouvre/ferme les enfants d'un MÉGAMENU TABS
     * Règle : seul le premier enfant s'ouvre quand le parent s'ouvre
     *
     * @param {HTMLElement} submenu
     * @param {boolean} open
     */
    _setMegaTabsSubmenuOpen(submenu, open) {
        const toggles = submenu.querySelectorAll(MenubarNavigation.SEL.scopeLiToggle);

        if (toggles.length === 0) return;

        const firstToggle = toggles[0];

        for (const toggle of toggles) {
            const childSubmenu = this._getSubmenu(toggle);

            if (!childSubmenu) continue;

            const shouldOpen = open && toggle === firstToggle;

            toggle.setAttribute('aria-expanded', String(shouldOpen));
            childSubmenu.hidden = !shouldOpen;
        }
    }

    /**
     * Ferme les menus frères d'un toggle
     * @param {HTMLElement} toggle
     */
    _closeSiblingSubmenus(toggle) {
        const parentLi = toggle.closest(MenubarNavigation.SEL.li);
        const parentUl = parentLi && parentLi.parentElement;
        if (!parentUl) return;

        const siblings = parentUl.querySelectorAll(MenubarNavigation.SEL.scopeLiToggle);
        for (const sibling of siblings) {
            if (sibling !== toggle) {
                const submenu = this._getSubmenu(sibling);
                if (submenu) this._closeSubmenuDeep(sibling, submenu);
            }
        }
    }

    /**
     * Ferme un submenu et tous ses enfants récursivement
     * @param {HTMLElement} toggle
     * @param {HTMLElement} submenu
     */
    _closeSubmenuDeep(toggle, submenu) {
        const childToggles = submenu.querySelectorAll(MenubarNavigation.SEL.toggle);
        for (const childToggle of childToggles) {
            const childSubmenu = this._getSubmenu(childToggle);
            if (childSubmenu) this._closeSubmenuDeep(childToggle, childSubmenu);
        }

        toggle.setAttribute('aria-expanded', 'false');
        submenu.hidden = true;
    }

    /**
     * Ferme tous les submenus ouverts
     */
    _closeAllSubmenus() {
        if (!this._menubar) return;

        const toggles = this._menubar.querySelectorAll(MenubarNavigation.SEL.toggle);
        for (const toggle of toggles) {
            const submenu = this._getSubmenu(toggle);
            if (submenu) this._closeSubmenuDeep(toggle, submenu);
        }
        this._openSubmenus = [];
    }

    // ─── Keyboard Navigation ──────────────────────────────────────────────────

    /**
     * Gère les événements keydown
     * @param {KeyboardEvent} e
     */
    _onKeydown(e) {
        const handlers = {
            'Escape': () => this._handleEscape(e),
            'ArrowLeft': () => this._handleArrowsHorizontal(e),
            'ArrowRight': () => this._handleArrowsHorizontal(e),
            'ArrowUp': () => this._handleArrowsVertical(e),
            'ArrowDown': () => this._handleArrowsVertical(e),
        };

        if (handlers[e.key]) {
            handlers[e.key]();
        }
    }

    /**
     * Gère Escape : ferme le dernier submenu ou la nav mobile
     * @param {KeyboardEvent} e
     */
    _handleEscape(e) {
        e.preventDefault();

        if (this._openSubmenus.length > 0) {
            const last = this._openSubmenus[this._openSubmenus.length - 1];
            const megaTabsToggle = this._getMegaTabsToggle(last.toggle);

            if (!this.isMobile && megaTabsToggle) {
                this._closeAllSubmenus();
                megaTabsToggle.focus();
                return;
            }

            const isMega = this._isMegaMenu(last.toggle);

            if (!this.isMobile && isMega) {
                this._closeAllSubmenus();
            } else {
                this._setSubmenuOpen(last.toggle, last.submenu, false);
            }

            last.toggle.focus();
        } else if (this.isMobile && this._isNavOpen()) {
            this._setNavOpen(false);
        }
    }

    /**
     * Gère les flèches horizontales : navigue entre items du menu principal
     * @param {KeyboardEvent} e
     */
    _handleArrowsHorizontal(e) {
        const topItem = this._closestTopItem(e.target);
        const toggle = topItem && topItem.querySelector(MenubarNavigation.SEL.scopeToggle);

        if (!toggle || e.target !== toggle) return;

        const items = Array.from(this._menubar.querySelectorAll(MenubarNavigation.SEL.topMenuItems));
        const idx = items.indexOf(toggle);

        if (idx === -1) return;

        const next = e.key === 'ArrowRight'
            ? items[(idx + 1) % items.length]
            : items[(idx - 1 + items.length) % items.length];

        if (next) next.focus();
        e.preventDefault();
    }

    /**
     * Gère les flèches verticales : navigue dans les submenus
     * @param {KeyboardEvent} e
     */
    _handleArrowsVertical(e) {
        const submenuEl = e.target.closest(MenubarNavigation.SEL.submenu);

        if (submenuEl && submenuEl.hidden === false) {
            e.preventDefault();
            const focusables = this._getFocusablesInSubmenu(submenuEl);
            const idx = focusables.indexOf(e.target);

            if (idx > -1) {
                const nextIdx = e.key === 'ArrowDown'
                    ? (idx < focusables.length - 1 ? idx + 1 : 0)
                    : (idx > 0 ? idx - 1 : focusables.length - 1);
                if (focusables[nextIdx]) focusables[nextIdx].focus();
            }
            return;
        }

        const toggle = e.target.closest(MenubarNavigation.SEL.toggle);
        if (!toggle) return;

        const submenu = this._getSubmenu(toggle);
        if (!submenu || submenu.hidden === true) return;

        e.preventDefault();
        let focusables = this._getFocusablesInSubmenu(submenu);
        focusables = focusables.filter((el) => el.id !== toggle.id);

        if (focusables.length > 0) {
            const target = e.key === 'ArrowDown' ? focusables[0] : focusables[focusables.length - 1];
            if (target) target.focus();
        }
    }

    /**
     * Récupère tous les éléments focusables visibles dans une zone
     * @param {HTMLElement} submenuEl
     * @returns {HTMLElement[]}
     */
    _getFocusablesInSubmenu(submenuEl) {
        const megaMenu = submenuEl.closest(MenubarNavigation.SEL.megaMenu);
        const searchArea = megaMenu || submenuEl;
        const focusables = [];

        const nodeList = searchArea.querySelectorAll(MenubarNavigation.SEL.focusable);
        for (const el of nodeList) {
            const style = window.getComputedStyle(el);
            if (style.display !== 'none' && style.visibility !== 'hidden') {
                focusables.push(el);
            }
        }

        return focusables;
    }

    /**
     * Gère le clic sur un toggle
     * @param {HTMLElement} toggle
     */
    _onToggleClick(toggle) {
        const submenu = this._getSubmenu(toggle);
        if (!submenu) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';

        this._setSubmenuOpen(toggle, submenu, !isOpen);
    }
}

// ─── Sélecteurs ──────────────────────────────────────────────────────────────

/**
 * @type {Object<string, string>} Tous les sélecteurs CSS utilisés
 */
MenubarNavigation.SEL = {
    nav: '#primary-menu',
    burger: '#burger-menu',
    menubar: '.menu-items-list',
    main: '#main',
    footer: '#footer',
    toggle: 'button[aria-controls]',
    submenu: 'ul.sub-menu',
    li: 'li',
    megaToggle: 'is-mega-menu',
    megaMenu: 'li.mega-menu',
    megaTabs: '.mega-menu-tabs',
    megaTabToggle: 'is-mega-menu-tabs',
    megaTabsClass: 'mega-menu-tabs',
    scopeToggle: ':scope > a, :scope > button[aria-controls]',
    scopeLiToggle: ':scope > li > button[aria-controls]',
    topMenuItems: ':scope > li > a, :scope > li > button[aria-controls]',
    focusable: 'a, button'
};

/**
 * CountriesPanel - Gère le sélecteur de pays avec focus trap
 */
class CountriesPanel {
    constructor(panelId) {
        this.panel = document.querySelector(`#${panelId}`);
        this.toggleOpen = document.querySelector(`#btn-countries[aria-controls="${panelId}"]`);
        this.toggleClose = document.querySelector(`#btn-countries-close[aria-controls="${panelId}"]`);
        this.container = this.panel.closest('.all-countries');
        this._focusTrapHandler = null;

        if (!this.panel || !this.toggleOpen || !this.toggleClose || !this.container) {
            console.warn('CountriesPanel: Elements manquants');
            return;
        }

        this._bindToggleOpen();
        this._bindToggleClose();
        this._bindOutsideClick();
        this._bindEscape();
        this._bindHover();
        this._bindFocusLoss();
    }

    // ─── Binding ──────────────────────────────────────────────────────────────

    /**
     * Bind le clic sur le toggle d'ouverture
     */
    _bindToggleOpen() {
        this.toggleOpen.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isOpen = this.toggleOpen.getAttribute('aria-expanded') === 'true';
            this._setOpen(!isOpen);
        });
    }

    /**
     * Bind le clic sur le bouton de fermeture
     */
    _bindToggleClose() {
        this.toggleClose.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._setOpen(false);
        });
    }

    /**
     * Bind le hover (desktop uniquement)
     */
    _bindHover() {
        if (!window.matchMedia('(hover: hover)').matches) return;

        this.container.addEventListener('mouseenter', () => {
            if (this.toggleOpen.getAttribute('aria-expanded') !== 'true') {
                this._setOpen(true);
            }
        });

        this.container.addEventListener('mouseleave', () => {
            if (this.toggleOpen.getAttribute('aria-expanded') === 'true') {
                if (!this.container.matches(':hover')) {
                    this._setOpen(false);
                }
            }
        });
    }

    /**
     * Ferme au clic en dehors
     */
    _bindOutsideClick() {
        document.addEventListener('pointerdown', (e) => {
            // Si le click est sur un bouton du panel, ne rien faire
            if (this.toggleOpen.contains(e.target) || this.toggleClose.contains(e.target)) {
                return;
            }

            // Si le click est en dehors du container
            if (!this.container.contains(e.target)) {
                if (this.toggleOpen.getAttribute('aria-expanded') === 'true') {
                    this._setOpen(false);
                }
            }
        });
    }

    /**
     * Ferme avec Escape
     */
    _bindEscape() {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.toggleOpen.getAttribute('aria-expanded') === 'true') {
                e.preventDefault();
                this._setOpen(false);
                this.toggleOpen.focus();
            }
        });
    }

    // ─── State Management ──────────────────────────────────────────────────────

    /**
     * Ouvre/ferme le panel avec focus trap
     * @param {boolean} open
     */
    _setOpen(open) {
        // Synchroniser les deux boutons
        this.toggleOpen.setAttribute('aria-expanded', String(open));
        this.toggleClose.setAttribute('aria-expanded', String(open));
        this.panel.inert = !open;

        if (open) {
            this._bindFocusTrap();
            requestAnimationFrame(() => {
                const firstFocusable = this.panel.querySelector(CountriesPanel.SEL.focusable);
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            });
        } else {
            this._unbindFocusTrap();
            this.toggleOpen.focus();
        }
    }

    /**
     * Active le focus trap dans le panel
     */
    _bindFocusTrap() {
        // Focus trap seulement en mobile
        if (window.matchMedia('(hover: hover)').matches) return;

        if (this._focusTrapHandler) return;

        this._focusTrapHandler = (e) => {
            if (e.key !== 'Tab') return;
            // ... reste du code
        };

        document.addEventListener('keydown', this._focusTrapHandler);
    }

    _bindFocusLoss() {
        this.panel.addEventListener('focusout', (e) => {
            // Vérifier que le focus est vraiment sorti du panel
            setTimeout(() => {
                if (!this.panel.contains(document.activeElement)) {
                    this._setOpen(false);
                }
            }, 0);
        });
    }

    /**
     * Désactive le focus trap
     */
    _unbindFocusTrap() {
        if (this._focusTrapHandler) {
            document.removeEventListener('keydown', this._focusTrapHandler);
            this._focusTrapHandler = null;
        }
    }
}

// ─── Sélecteurs ──────────────────────────────────────────────────────────────

CountriesPanel.SEL = {
    focusable: 'a, button'
};

// ─── Init ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', function () {
    const navNode = document.querySelector(MenubarNavigation.SEL.nav);
    if (navNode) {
        new MenubarNavigation(navNode);
    }

    // Initialiser le panel des pays
    const countriesPanelId = 'all-countries-panel';
    if (document.querySelector(`#${countriesPanelId}`)) {
        new CountriesPanel(countriesPanelId);
    }
});
